import { ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  side?: 'right' | 'left';
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  side = 'right',
}: DrawerProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/30" />
        <Dialog.Content
          className={cn(
            'fixed top-0 z-50 h-full w-[480px] max-w-full bg-white border-ink-200',
            'flex flex-col focus:outline-none',
            side === 'right'
              ? 'right-0 border-l'
              : 'left-0 border-r',
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-ink-200 shrink-0">
            <div className="min-w-0">
              <Dialog.Title className="serif-jp text-base font-semibold text-ink-900 truncate">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-ink-600">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="shrink-0 rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
